import { BackCoverFooter, PageShell, TopNav } from "@/app/components/brand-scaffold";
import { siteHeaderActions, siteNavItems } from "@/app/content";
import { CodeBlock, NextCta, Note, QuickStartHero, Step } from "./QuickStartShared";

const SAMPLE_REQUEST = `{
  "hotline_id": "local.delegated-execution.workspace-summary.v1",
  "input": {
    "text": "summarize my workspace"
  }
}`;

const SAMPLE_SUCCESS = `{
  "request_id": "req_xxx",
  "status": "SUCCEEDED",
  "result_package": {
    "request_id": "req_xxx",
    "responder_id": "responder_xxx",
    "hotline_id": "local.delegated-execution.workspace-summary.v1",
    "status": "ok",
    "output": {
      "summary": "..."
    },
    "timing": {
      "elapsed_ms": 123
    }
  }
}`;

const SAMPLE_ERROR = `{
  "request_id": "req_xxx",
  "status": "FAILED",
  "result_package": {
    "hotline_id": "local.delegated-execution.workspace-summary.v1",
    "status": "error",
    "error": {
      "code": "AUTH_TOKEN_INVALID",
      "message": "task token is missing or invalid"
    }
  }
}`;

const SAMPLE_REGISTRATION = `{
  "hotline_id": "com.example.workspace-summary.v1",
  "summary": "Summarize one workspace snapshot for an external Caller.",
  "input_schema": {
    "type": "object",
    "properties": {
      "workspace_path": { "type": "string" }
    },
    "required": ["workspace_path"]
  },
  "output_schema": {
    "type": "object",
    "properties": {
      "summary": { "type": "string" }
    },
    "required": ["summary"]
  },
  "examples": [
    {
      "input": { "workspace_path": "/tmp/demo" },
      "output": { "summary": "..." }
    }
  ]
}`;

export default function ProtocolExamplesPage() {
  return (
    <PageShell>
      <TopNav navItems={siteNavItems} actionItems={siteHeaderActions} homeHref="/" />

      <QuickStartHero
        role="Protocol · Examples"
        roleColor="#3B82F6"
        titleTop="协议层最小"
        titleBottom="示例集合"
        lead="这页不讲完整流程，只给你 4 个最稳定、最容易被搜索和答案引擎引用的协议示例：最小 request、成功 result_package、常见 error、以及 hotline registration payload。"
        meta={["Request", "result_package", "Error", "Registration"]}
      />

      <main className="relative z-10 mx-auto w-full max-w-5xl px-4 py-10 sm:px-6 lg:px-8 lg:py-16">
        <div className="mb-8 border-4 border-black bg-white/85 p-5 shadow-[6px_6px_0px_0px_rgba(0,0,0,1)]">
          <p className="text-xs font-black uppercase tracking-[0.22em] text-zinc-500">这页适合谁</p>
          <h2 className="mt-2 text-xl font-black text-black sm:text-2xl">适合需要“先看 shape，再跑命令”的人</h2>
          <p className="mt-4 text-sm font-bold leading-7 text-zinc-800">
            如果你已经知道 CALL ANYTHING 是什么，但还没记住 request、result_package、error 和 registration payload
            分别长什么样，这页就是最短入口。它也是当前站点里最适合被引用的协议证据页之一。
          </p>
        </div>

        <div className="space-y-8">
          <Step
            num="01"
            title="最小 request"
            intent="Caller 视角里，第一件事不是理解所有运行时细节，而是看清它最少要提交哪些字段。"
          >
            <CodeBlock language="json" caption="Minimal request shape">{SAMPLE_REQUEST}</CodeBlock>
            <Note>
              真正业务场景里 <code className="font-mono">input</code> 的 shape 由该 hotline 的
              <code className="font-mono"> input_schema </code>决定，但外层调用模式保持稳定。
            </Note>
          </Step>

          <Step
            num="02"
            title="成功 result_package"
            intent="所有 Hotline 的核心价值之一是共享同一种外层结果包装。Agent 只需要学习一次。"
          >
            <CodeBlock language="json" caption="Successful result_package">{SAMPLE_SUCCESS}</CodeBlock>
            <div className="border-2 border-black bg-white/80 p-4 text-sm font-bold text-zinc-800">
              <p>先看这几个字段：</p>
              <ul className="mt-2 grid gap-1.5 sm:grid-cols-2">
                <li>· <code className="font-mono">status</code>：请求生命周期状态</li>
                <li>· <code className="font-mono">result_package.status</code>：协议层 ok / error</li>
                <li>· <code className="font-mono">hotline_id</code>：实际调用的能力版本</li>
                <li>· <code className="font-mono">timing.elapsed_ms</code>：可观测与结算基础字段</li>
              </ul>
            </div>
          </Step>

          <Step
            num="03"
            title="常见错误返回"
            intent="GEO 和接入实践里，错误 shape 和成功 shape 一样重要，因为 Agent 需要知道什么时候重试、什么时候停止。"
          >
            <CodeBlock language="json" caption="Common error shape">{SAMPLE_ERROR}</CodeBlock>
            <Note variant="warn">
              先认 <code className="font-mono">error.code</code>，再认 message。对于自动化系统，结构化 code 比自由文本更重要。
            </Note>
          </Step>

          <Step
            num="04"
            title="最小 hotline registration payload"
            intent="Responder 侧最容易出错的不是运行时，而是 contract packaging。先看清 payload，很多问题会提前消失。"
          >
            <CodeBlock language="json" caption="Minimal registration payload">{SAMPLE_REGISTRATION}</CodeBlock>
            <Note>
              最关键的字段是 <code className="font-mono">hotline_id</code>、<code className="font-mono">summary</code>、
              <code className="font-mono">input_schema</code>、<code className="font-mono">output_schema</code> 和 examples。
            </Note>
          </Step>
        </div>

        <div className="mt-12 border-[6px] border-black bg-[#3B82F6]/15 p-6 shadow-[10px_10px_0px_0px_rgba(0,0,0,1)] sm:p-8">
          <h2 className="text-2xl font-black uppercase text-black sm:text-3xl">继续往哪看</h2>
          <p className="mt-3 text-sm font-bold text-zinc-900 sm:text-base">
            这页解决的是“先记住 shape”。如果你要看真实命令和完整上下文，再顺着下面两条路走。
          </p>
          <div className="mt-5 flex flex-wrap gap-3">
            <NextCta href="/docs/quick-start-caller/" label="看 Caller Quick Start" tone="lime" />
            <NextCta href="/docs/quick-start-responder/" label="看 Responder Quick Start" tone="blue" />
            <NextCta href="/docs/" label="回 Docs 总览" tone="white" />
          </div>
        </div>
      </main>

      <BackCoverFooter />
    </PageShell>
  );
}
